import { importLibrary } from "@googlemaps/js-api-loader";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { debounce } from '@mui/material/utils';
import parse from "autosuggest-highlight/parse";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { geocodeByPlaceID } from "utils/Geocode";

// CHANGED: Set options globally

const GoogleMapsAutocomplete = (props) => {
  const { filters, setFilters } = props;
  const { t } = useTranslation();
  const [value, setValue] = React.useState(filters.address ? filters.address : null);
  const [inputValue, setInputValue] = React.useState("A");
  const [options, setOptions] = React.useState([]);
  const [autocompleteService, setAutocompleteService] = React.useState(null);
  const [geocoder, setGeocoder] = React.useState(null);

  const fetch = React.useMemo(
    () =>
      debounce((request, callback) => {
        if (autocompleteService) {
          autocompleteService.getPlacePredictions(request, callback);
        }
      }, 400),
    [autocompleteService]
  );

  // Initialize Google Maps services
  React.useEffect(() => {
    let active = true;

    const initializeServices = async () => {
      try {
        // Check if Google Maps is already loaded
        if (!window.google || !window.google.maps) {
          console.warn('Google Maps API not loaded yet');
          return;
        }

        const [places, geocoding] = await Promise.all([
          importLibrary("places"),
          importLibrary("geocoding")
        ]);

        if (active) {
          setAutocompleteService(new places.AutocompleteService());
          setGeocoder(new geocoding.Geocoder());
        }
      } catch (error) {
        console.error("Error loading Google Maps libraries:", error);
      }
    };

    initializeServices();

    return () => {
      active = false;
    };
  }, []);

  // Fetch places when input changes
  React.useEffect(() => {
    let active = true;

    if (inputValue === "" || !autocompleteService) {
      setOptions(value ? [value] : []);
      return;
    }

    fetch({ input: inputValue }, (results) => {
      if (active) {
        let newOptions = [];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }
        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch, autocompleteService]);

  // Save coordinates when value changes
  React.useEffect(() => {
    const callGeoCoder = async () => {
      try {
        if (value !== null && value.place_id !== undefined && geocoder) {
          const [{ geometry }] = await geocodeByPlaceID(geocoder, value.place_id);
          const coordinates = {
            lat: geometry.location.lat(),
            lng: geometry.location.lng(),
          };
          setFilters({
            ...filters,
            address: value.description,
            coordinates,
          });
        }
      } catch (e) {
        console.error(e);
      }
    };

    if (value && value.place_id) {
      callGeoCoder();
    }
  }, [value, geocoder, filters, setFilters]);

  return (
    <Autocomplete
      id="google-maps"
      getOptionLabel={(option) => (typeof option === "string" ? option : option.description)}
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value}
      onChange={(event, newValue) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => <TextField {...params} label={t("Add a location")} />}
      renderOption={(props, option) => {
        let matches = [];
        if (option.structured_formatting.main_text_matched_substrings !== undefined) {
          matches = option.structured_formatting.main_text_matched_substrings;
        };
        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match) => [match.offset, match.offset + match.length])
        );
        return (
          <li {...props}>
            <Grid container alignItems="center">
              <Grid>
                <Box component={LocationOnIcon} sx={{ color: "text.secondary", mr: 2 }} />
              </Grid>
              <Grid size="grow">
                {parts.map((part, index) => (
                  <span
                    key={index}
                    style={{
                      fontWeight: part.highlight ? 700 : 400,
                    }}
                  >
                    {part.text}
                  </span>
                ))}
                <Typography variant="body2" color="text.secondary">
                  {option.structured_formatting.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
};

export default GoogleMapsAutocomplete;